/**
 * FlexEngine — Dexie Repository
 *
 * Concrete implementation of IFlexRepository backed by IndexedDB via Dexie.
 * A future Supabase adapter only needs to implement IFlexRepository — no app
 * logic changes required.
 */

import { db } from './db';
import { BUILT_IN_EXERCISES } from '@/data/exercises.seed';
import type {
  IFlexRepository,
  Exercise,
  WorkoutSession,
  LoggedSet,
  MuscleState,
  GymProfile,
  AppSettings,
  Equipment,
  MuscleGroup,
  ExportPayload,
  SplitDay,
  SplitType,
  PersonalRecord,
} from '@/types';

// ── Decay constants (per hour) ────────────────────────────────
// Tuned so each muscle recovers fully (F→~0.01) within its window.
// Larger/denser muscles recover slower (72h); smaller ones faster (48h).
const DECAY_CONSTANTS: Record<MuscleGroup, number> = {
  chest:        0.0556, // 72h full recovery
  front_delt:   0.0625, // 64h
  lateral_delt: 0.0694, // 60h
  rear_delt:    0.0694, // 60h
  triceps:      0.0694, // 60h
  biceps:       0.0694, // 60h
  forearms:     0.0833, // 50h
  lats:         0.0556, // 72h
  traps:        0.0625, // 64h
  rhomboids:    0.0625, // 64h
  lower_back:   0.0476, // 84h — slow recovery, high injury risk
  quads:        0.0476, // 84h
  hamstrings:   0.0476, // 84h
  glutes:       0.0556, // 72h
  calves:       0.0833, // 50h — recover fast, train often
  core:         0.0833, // 50h
  hip_flexors:  0.0694, // 60h
};

// ── Default App State ─────────────────────────────────────────

function defaultSettings(): AppSettings {
  return {
    id: 'singleton',
    splitType: 'push_pull',
    nextSplitDay: 'push',
    activeGymProfileId: 'profile_default',
    weightUnit: 'lbs',
    audioEnabled: true,
    weeklyVolumeTarget: 15,
    sessionVolumeCap: 8,
    sessionSetCap: 22,
    trainingGoal: 'hypertrophy',
    experienceLevel: 'intermediate',
    preferredDurationMinutes: 60,
    onboardingComplete: false,
    deloadRecommended: false,
    theme: 'dark',
    notificationsEnabled: false,
    notificationTime: '08:00',
  };
}

function defaultGymProfile(): GymProfile {
  return {
    id: 'profile_default',
    name: 'My Gym',
    availableEquipment: ['barbell', 'dumbbell', 'cable', 'machine', 'bodyweight', 'ez_bar'],
    isActive: true,
    createdAt: new Date().toISOString(),
  };
}

function defaultMuscleState(muscle: MuscleGroup): MuscleState {
  return {
    id: muscle,
    muscle,
    fatigueAtUpdate: 0,
    lastUpdatedAt: new Date().toISOString(),
    decayConstantPerHour: DECAY_CONSTANTS[muscle],
    isInjured: false,
    weeklyHardSets: 0,
    weeklyWindowStart: new Date().toISOString(),
  };
}

const ALL_MUSCLES: MuscleGroup[] = Object.keys(DECAY_CONSTANTS) as MuscleGroup[];

// ── Repository Implementation ─────────────────────────────────

export class DexieRepository implements IFlexRepository {

  // ── Seeding ──────────────────────────────────────────────────

  async seed(): Promise<void> {
    const exerciseCount = await db.exercises.count();
    if (exerciseCount === 0) {
      await db.exercises.bulkPut(BUILT_IN_EXERCISES);
    }

    const settingsExist = await db.settings.get('singleton');
    if (!settingsExist) {
      await db.settings.put(defaultSettings());
    }

    const profileCount = await db.gymProfiles.count();
    if (profileCount === 0) {
      await db.gymProfiles.put(defaultGymProfile());
    }

    // Ensure a MuscleState row exists for every muscle
    await db.transaction('rw', db.muscleStates, async () => {
      for (const muscle of ALL_MUSCLES) {
        const existing = await db.muscleStates.get(muscle);
        if (!existing) {
          await db.muscleStates.put(defaultMuscleState(muscle));
        }
      }
    });
  }

  // ── Exercises ────────────────────────────────────────────────

  async getExercise(id: string): Promise<Exercise | undefined> {
    return db.exercises.get(id);
  }

  async getAllExercises(): Promise<Exercise[]> {
    return db.exercises.toArray();
  }

  async getExercisesByEquipment(equipment: Equipment[]): Promise<Exercise[]> {
    // Return exercises where at least one required piece is in the available list
    const all = await db.exercises.toArray();
    return all.filter((ex) =>
      ex.equipment.some((eq) => equipment.includes(eq)),
    );
  }

  async upsertExercise(exercise: Exercise): Promise<void> {
    await db.exercises.put(exercise);
  }

  // ── Workout Sessions ─────────────────────────────────────────

  async createWorkout(session: WorkoutSession): Promise<void> {
    await db.workouts.add(session);
  }

  async updateWorkout(id: string, patch: Partial<WorkoutSession>): Promise<void> {
    await db.workouts.update(id, patch);
  }

  async getWorkout(id: string): Promise<WorkoutSession | undefined> {
    return db.workouts.get(id);
  }

  async getRecentWorkouts(limit: number): Promise<WorkoutSession[]> {
    return db.workouts
      .orderBy('startedAt')
      .reverse()
      .limit(limit)
      .toArray();
  }

  async getAllWorkouts(): Promise<WorkoutSession[]> {
    return db.workouts.orderBy('startedAt').reverse().toArray();
  }

  // ── Logged Sets ──────────────────────────────────────────────

  async logSet(set: LoggedSet): Promise<PersonalRecord | null> {
    await db.loggedSets.add(set);

    let pr: PersonalRecord | null = null;

    if (!set.isWarmup) {
      const exercise = await db.exercises.get(set.exerciseId);
      if (!exercise) return null;

      // Fatigue accumulation
      await db.transaction('rw', db.muscleStates, async () => {
        for (const allocation of exercise.muscles) {
          await this._accumulateFatigue(
            allocation.muscle,
            allocation.fraction,
            set.rpe >= 7 ? allocation.fraction : 0,
          );
        }
      });

      // PR detection
      pr = await this._checkPersonalRecord(set, exercise.name);
    }

    return pr;
  }

  private async _checkPersonalRecord(
    set: LoggedSet,
    exerciseName: string,
  ): Promise<PersonalRecord | null> {
    const new1RM = set.estimated1RM;
    if (new1RM <= 0) return null;

    const prevSets = await db.loggedSets
      .where('exerciseId').equals(set.exerciseId)
      .filter((s) => !s.isWarmup && s.id !== set.id)
      .toArray();

    const prev1RM = prevSets.length > 0
      ? Math.max(...prevSets.map((s) => s.estimated1RM))
      : 0;

    if (new1RM <= prev1RM) return null;

    const pr: PersonalRecord = {
      id: `pr_${set.id}`,
      exerciseId: set.exerciseId,
      exerciseName,
      previous1RM: Math.round(prev1RM * 10) / 10,
      new1RM: Math.round(new1RM * 10) / 10,
      weightLbs: set.weightLbs,
      reps: set.reps,
      achievedAt: set.loggedAt,
      workoutId: set.workoutId,
    };

    await db.personalRecords.put(pr);
    return pr;
  }

  async getPersonalRecords(exerciseId?: string): Promise<PersonalRecord[]> {
    if (exerciseId) {
      return db.personalRecords
        .where('exerciseId').equals(exerciseId)
        .reverse().toArray();
    }
    return db.personalRecords.orderBy('achievedAt').reverse().toArray();
  }

  async getBestPRPerExercise(): Promise<Map<string, PersonalRecord>> {
    const all = await db.personalRecords.orderBy('achievedAt').toArray();
    const map = new Map<string, PersonalRecord>();
    for (const pr of all) {
      const existing = map.get(pr.exerciseId);
      if (!existing || pr.new1RM > existing.new1RM) map.set(pr.exerciseId, pr);
    }
    return map;
  }

  /** Internal: decay existing fatigue then accumulate new stimulus */
  private async _accumulateFatigue(
    muscle: MuscleGroup,
    fatigueLoad: number,   // 0.0 – 1.0 (fraction of max stimulus)
    volumeLoad: number,    // fractional hard-set contribution
  ): Promise<void> {
    let state = await db.muscleStates.get(muscle);
    if (!state) {
      state = defaultMuscleState(muscle);
    }

    const now = new Date();
    const lastUpdate = new Date(state.lastUpdatedAt);
    const hoursElapsed = (now.getTime() - lastUpdate.getTime()) / 3_600_000;

    // Decay current fatigue before adding new stimulus
    const decayed = state.fatigueAtUpdate * Math.exp(-state.decayConstantPerHour * hoursElapsed);

    // Each working set adds a fraction of max fatigue (capped at 1.0)
    // Stimulus per set ≈ 0.15 units per primary set (so ~7 sets → F≈1.0)
    const BASE_FATIGUE_PER_SET = 0.15;
    const newFatigue = Math.min(1.0, decayed + BASE_FATIGUE_PER_SET * fatigueLoad);

    // Rolling weekly hard-set count — reset if window expired
    const windowStart = new Date(state.weeklyWindowStart);
    const daysSinceWindow = (now.getTime() - windowStart.getTime()) / 86_400_000;
    const weeklyHardSets =
      daysSinceWindow >= 7
        ? volumeLoad
        : state.weeklyHardSets + volumeLoad;
    const weeklyWindowStart =
      daysSinceWindow >= 7 ? now.toISOString() : state.weeklyWindowStart;

    await db.muscleStates.put({
      ...state,
      fatigueAtUpdate: newFatigue,
      lastUpdatedAt: now.toISOString(),
      weeklyHardSets,
      weeklyWindowStart,
    });
  }

  async updateSet(id: string, patch: Partial<LoggedSet>): Promise<void> {
    await db.loggedSets.update(id, patch);
  }

  async deleteSet(id: string): Promise<void> {
    await db.loggedSets.delete(id);
  }

  async getSetsForWorkout(workoutId: string): Promise<LoggedSet[]> {
    return db.loggedSets
      .where('workoutId')
      .equals(workoutId)
      .sortBy('loggedAt');
  }

  async getSetsForExercise(exerciseId: string, limit = 100): Promise<LoggedSet[]> {
    return db.loggedSets
      .where('exerciseId')
      .equals(exerciseId)
      .reverse()
      .limit(limit)
      .sortBy('loggedAt');
  }

  async getRecentSetsForExercise(exerciseId: string, days: number): Promise<LoggedSet[]> {
    const cutoff = new Date(Date.now() - days * 86_400_000).toISOString();
    return db.loggedSets
      .where('exerciseId')
      .equals(exerciseId)
      .filter((s) => s.loggedAt >= cutoff)
      .toArray();
  }

  // ── Muscle State ─────────────────────────────────────────────

  async getMuscleState(muscle: MuscleGroup): Promise<MuscleState | undefined> {
    return db.muscleStates.get(muscle);
  }

  async getAllMuscleStates(): Promise<MuscleState[]> {
    return db.muscleStates.toArray();
  }

  async upsertMuscleState(state: MuscleState): Promise<void> {
    await db.muscleStates.put(state);
  }

  // ── Gym Profiles ─────────────────────────────────────────────

  async getActiveGymProfile(): Promise<GymProfile | undefined> {
    // Dexie stores booleans as 0/1 in indexes; filter in-memory for reliability
    const all = await db.gymProfiles.toArray();
    return all.find((p) => p.isActive) ?? all[0];
  }

  async getAllGymProfiles(): Promise<GymProfile[]> {
    return db.gymProfiles.toArray();
  }

  async upsertGymProfile(profile: GymProfile): Promise<void> {
    await db.gymProfiles.put(profile);
  }

  async setActiveGymProfile(id: string): Promise<void> {
    await db.transaction('rw', db.gymProfiles, async () => {
      // Deactivate all profiles
      await db.gymProfiles.toCollection().modify({ isActive: false });
      await db.gymProfiles.update(id, { isActive: true });
    });

    // Update settings
    await db.settings.update('singleton', { activeGymProfileId: id });
  }

  // ── Settings ─────────────────────────────────────────────────

  async getSettings(): Promise<AppSettings | undefined> {
    return db.settings.get('singleton');
  }

  async updateSettings(patch: Partial<AppSettings>): Promise<void> {
    const existing = await db.settings.get('singleton');
    if (existing) {
      await db.settings.update('singleton', patch);
    } else {
      await db.settings.put({ ...defaultSettings(), ...patch });
    }
  }

  // ── Backup / Restore ─────────────────────────────────────────

  async exportAllData(): Promise<ExportPayload> {
    const [exercises, workouts, loggedSets, muscleStates, gymProfiles, settingsRow, personalRecords] =
      await Promise.all([
        db.exercises.toArray(),
        db.workouts.toArray(),
        db.loggedSets.toArray(),
        db.muscleStates.toArray(),
        db.gymProfiles.toArray(),
        db.settings.get('singleton'),
        db.personalRecords.toArray(),
      ]);

    return {
      schemaVersion: 2,
      exportedAt: new Date().toISOString(),
      exercises,
      workouts,
      loggedSets,
      muscleStates,
      gymProfiles,
      settings: settingsRow ?? null,
      personalRecords,
    };
  }

  async importAllData(payload: ExportPayload): Promise<void> {
    await db.transaction(
      'rw',
      [db.exercises, db.workouts, db.loggedSets, db.muscleStates, db.gymProfiles, db.settings, db.personalRecords],
      async () => {
        await db.exercises.clear();
        await db.workouts.clear();
        await db.loggedSets.clear();
        await db.muscleStates.clear();
        await db.gymProfiles.clear();
        await db.settings.clear();
        await db.personalRecords.clear();

        await db.exercises.bulkPut(payload.exercises);
        await db.workouts.bulkPut(payload.workouts);
        await db.loggedSets.bulkPut(payload.loggedSets);
        await db.muscleStates.bulkPut(payload.muscleStates);
        await db.gymProfiles.bulkPut(payload.gymProfiles);
        if (payload.settings) await db.settings.put(payload.settings);
        if (payload.personalRecords?.length) {
          await db.personalRecords.bulkPut(payload.personalRecords);
        }
      },
    );
  }

  // ── Split Day Advancement ─────────────────────────────────────

  async advanceSplitDay(): Promise<SplitDay> {
    const settings = await this.getSettings();
    const splitType = settings?.splitType ?? 'push_pull';
    const current   = settings?.nextSplitDay ?? 'push';

    // Define the cycle for each split type
    const cycles: Record<SplitType, SplitDay[]> = {
      push_pull:      ['push', 'pull'],
      push_pull_legs: ['push', 'pull', 'legs'],
      upper_lower:    ['upper', 'lower'],
      full_body:      ['full_body_day'],
    };

    const cycle = cycles[splitType];
    const idx   = cycle.indexOf(current as SplitDay);
    const next  = cycle[(idx + 1) % cycle.length] as SplitDay;
    await this.updateSettings({ nextSplitDay: next });
    return next;
  }
}

// Singleton repository instance
export const repository = new DexieRepository();
